import { useMutation, useQuery } from 'react-query';
import {
  ApiKeysListParams,
  companies,
  UpdateApiKeyStatusBody,
} from '../../../API/TT_ELD/Users/company';
import { useRef } from 'react';
import { AxiosError } from 'axios';
import { ErrorNotification } from '../../../Utils/data';
import { useProjectData } from '../../useProjectData';
import { cameraEnabled } from '../../../Utils/cameraEnabled';

export const useCompaniesData = (
  companyName?: string,
  usdotId?: string | number,
  sort?: boolean,
  skip?: string,
  isDisabled?: string,
  ownerId?: string,
  isDispute?: string,
  uid?: string,
  isActive?: boolean,
  stripeCustomerId?: string | number,
  publicId?: string | number,
  isContract?: boolean,
) => {
  return useQuery(
    [
      `companies/${companyName || 'all'}`,
      companyName,
      usdotId,
      sort,
      skip,
      isDisabled,
      ownerId,
      isDispute,
      uid,
      isActive,
      stripeCustomerId,
      publicId,
      isContract,
    ],
    () =>
      companies.read(
        companyName,
        usdotId,
        sort,
        skip,
        isDisabled,
        ownerId,
        isDispute,
        uid,
        isActive,
        stripeCustomerId,
        publicId,
        isContract,
      ),
    { refetchOnWindowFocus: false },
  );
};

export const useTrialingSubscriptions = (
  searchName?: string,
  usdot?: string,
  isActive?: string,
  skip?: string,
) => {
  return useQuery(
    ['free-trial-list', searchName, usdot, isActive, skip],
    () => companies.freeTrialList(searchName, usdot, isActive, skip),
    { refetchOnWindowFocus: false },
  );
};

export const useFreeTrialHistory = (companyId: string | number | undefined, page: number) => {
  return useQuery(
    ['free-trial-history', companyId, page],
    () => companies.freeTrialHistory(companyId as string | number, page),
    { refetchOnWindowFocus: false, enabled: !!companyId },
  );
};

export const useExpires = (
  companName: number | string,
  usdot: string,
  status: string,
  uid: string,
  skip: string | undefined,
) => {
  return useQuery(
    [`companies/expires/${companName || 'all'}`, companName, usdot, status, uid, skip],
    () => companies.expires(companName, usdot, status, uid, skip),
    { refetchOnWindowFocus: false },
  );
};

export const useCompanyNewsubscription = (
  companName: number | string,
  usdot: string,
  status: string,
  uid: string,
  skip: string | undefined,
) => {
  return useQuery(
    [
      `companies/companyNewsubscription/${companName || 'all'}`,
      companName,
      usdot,
      status,
      uid,
      skip,
    ],
    () => companies.companyNewsubscription(companName, usdot, status, uid, skip),
    { refetchOnWindowFocus: false },
  );
};
export const useCompanyData = (companyId: number | string | undefined): any => {
  return useQuery([`companies/${companyId}`, companyId], () => companies.companyData(companyId), {
    refetchOnWindowFocus: false,
  });
};
export const useGetPrice = (
  companyId: number | string | undefined,
  productId?: string,
): any => {
  return useQuery(
    [`getPrice/${companyId}`, companyId, productId],
    () => companies.getPrice(companyId, productId),
    { refetchOnWindowFocus: false },
  );
};

export const useGetProducts = (companyId: number | string | undefined): any => {
  return useQuery(
    [`getProducts/${companyId}`, companyId],
    () => companies.getProducts(companyId),
    { refetchOnWindowFocus: false },
  );
};
export const useVehicleData = (vehicleId: number | string | undefined): any => {
  return useQuery([`vehicles/${vehicleId}`, vehicleId], () => companies.vehicleData(vehicleId), {
    refetchOnWindowFocus: false,
  });
};

export const useVehicleSearch = (truck_number?: string | number, companyId?: string | number) => {
  return useQuery({
    queryKey: ['vehicleSearch', truck_number, companyId],
    queryFn: async () => {
      return await companies.searchVehicleData(truck_number, companyId);
    },
    enabled: true, // HAR DOIM ishlaydi
  });
};

export const useSubscription = (stripeId: string, companyId: string): any => {
  return useQuery(
    [`subscription/${stripeId}`, stripeId, companyId],
    () => companies.subscription(stripeId, companyId),
    { refetchOnWindowFocus: false },
  );
};
export const useGetHistorySubscription = (status: string, next: object, companyId: string): any => {
  const errorShownRef = useRef(false); // ⬅️ флаг

  return useQuery(
    [`getHistorySubscription/`, status, next, companyId],
    () => companies.getHistorySubscription(status, next, companyId),
    {
      refetchOnWindowFocus: false,
      retry: false,
      onError: (error: unknown) => {
        if (
          !errorShownRef.current &&
          error instanceof AxiosError &&
          error.response?.status === 404
        ) {
          ErrorNotification({
            status: error.response.status,
            message: error.response.data?.error?.message || 'Unknown error',
          });
          errorShownRef.current = true; // ⬅️ не показывать больше
        }
      },
    },
  );
};
export const useCheckInvoices = (getCompanyId: string | number | undefined): object => {
  return useQuery([`checkStripe/`, getCompanyId], () => companies.checkInvoices(getCompanyId), {
    refetchOnWindowFocus: false,
  });
};
export const useRenewInvoice = () => {
  return useMutation({
    mutationFn: (body: { invoiceId: string; companyId: string }) => companies.renewInvoice(body),
  });
};
export const useUsersMe = (companyId: number | string | undefined): any => {
  return useQuery([`userMe/${companyId}`, companyId], () => companies.userMe(companyId), {
    refetchOnWindowFocus: false,
  });
};
export const useStripListType = (): any => {
  return useQuery([`strip-list-type/`], () => companies.getStripListType(), {
    refetchOnWindowFocus: false,
  });
};

export const useEventNames = (enabled: boolean = true): any => {
  const { projectData } = useProjectData();
  return useQuery(['alerts-admin-event-names'], () => companies.getEventNames(), {
    refetchOnWindowFocus: false,
    enabled: enabled && cameraEnabled(projectData?.projectName),
  });
};

export const useUpdateCompanyAlert = () => {
  return useMutation({
    mutationFn: ({
      companyId,
      body,
    }: {
      companyId: number | string;
      body: {
        cameraAlertChatId?: string | null;
        cameraAlertEmail?: string | null;
        telegramChatId?: string | null;
        settings?: Record<string, boolean>;
      };
    }) => companies.companyPatchDataAlert(body, companyId),
  });
};

export const useUpdateMultipleCompanies = () => {
  return useMutation({
    mutationFn: (body: any) => companies.companyMultipleEdit(body),
  });
};

export const useSendTelegramTestMessage = () => {
  return useMutation({
    mutationFn: (body: { chatId: string; botType: string; message: string }) =>
      companies.sendTelegramTestMessage(body),
  });
};

// X-API-Keys

export const useApiKeysList = (params: ApiKeysListParams) => {
  return useQuery(['apiKeysList', params], () => companies.getApiKeysList(params), {
    refetchOnWindowFocus: false,
    enabled: !!params.companyId,
  });
};

export const useUpdateApiKeyStatus = () => {
  return useMutation({
    mutationFn: (body: UpdateApiKeyStatusBody) => companies.updateApiKeyStatus(body),
  });
};
